import { Component, ElementRef, HostBinding, Input, OnInit, ViewChild, inject } from '@angular/core'
import { MenuItem } from 'primeng/api'
import { Subscription } from 'rxjs'
import { AppMenuService } from '../../../services/app-menu.service'
import { IsActiveMatchOptions } from '@angular/router'
import { AppLayoutService } from '../../../services/app-layout.service'
import { animate, state, style, transition, trigger, AnimationEvent } from '@angular/animations'
import { BaseComponent } from '../../../../../components/base.component'
import { ZendeskService } from '../../../../../services/zendesk.service'

@Component({
  selector: 'app-menu-item',
  templateUrl: './menu-item.component.html',
  styleUrl: './menu-item.component.scss',
  animations: [
    trigger('children', [
      state('collapsed', style({
        height: '0'
      })),
      state('expanded', style({
        height: '*'
      })),
      state('hidden', style({
        display: 'none'
      })),
      state('visible', style({
        display: 'block'
      })),
      transition('collapsed <=> expanded', animate('400ms cubic-bezier(0.86, 0, 0.07, 1)'))
    ])
  ],
  standalone: false
})
export class MenuItemComponent extends BaseComponent implements OnInit {

  @Input() item!: MenuItem
  @Input() index!: number
  @Input() @HostBinding('class.layout-root-menuitem') root!: boolean
  @Input() parentKey!: string
  @Input() parentItem: MenuItemComponent
  @Input() isCollapsed: boolean = true;
  @ViewChild('submenu') submenu!: ElementRef

  active = false
  isCurrentRoute = false
  showSubmenu = false;

  menuSourceSubscription!: Subscription

  menuResetSubscription!: Subscription

  key: string = ""

  menuService: AppMenuService = inject(AppMenuService)
  appLayoutService: AppLayoutService = inject(AppLayoutService)
  zendeskService: ZendeskService = inject(ZendeskService)

  ngOnInit(): void {
    this.subscriptions.add(this.menuService.menuSource$.subscribe(value => {
      if (value.routeEvent) {
        this.active = (value.key === this.item.id || value.key.startsWith(this.item.id + '-')) ? true : false;
        if (this.active && this.item.id == value.key && this.item.routerLink && !this.item.items) {
          this.navigateToMenu(this.item)
        }
      }
      else {
        if (value.key === this.item.id || value.key.startsWith(this.item.id + '-')) {
          if (this.active && this.item.id == value.key && this.item.routerLink && !this.item.items) {
            this.navigateToMenu(this.item)
          }
        }
      }
    }))

    this.subscriptions.add(this.menuService.resetOtherSource$.subscribe((item) => {
      // Don't reset submenu items that don't have routerLink (like "Detail Reports")
      if (!this.item.routerLink && this.hasSubmenu()) {
        return; // Skip reset for submenu containers
      }

      if (this.item.id !== item.id && this.item.items?.filter(x => x.id == item.id).length == 0) {
        this.active = false;
        this.showSubmenu = false;
      }
    }))
  }

  generateIdFromLabel(label: string): string {
    if (!label) return '';
    return label.toLowerCase().replace(/\s+/g, '-');
  }

  getMenuItemId(): string {
    const baseId = this.generateIdFromLabel(this.item.label || '');
    return `menu-item-${baseId}`;
  }

  getSubmenuId(): string {
    const baseId = this.generateIdFromLabel(this.item.label || '');
    return `submenu-${baseId}`;
  }

  getListItemId(): string {
    const baseId = this.generateIdFromLabel(this.item.label || '');
    return `menu-list-item-${baseId}`;
  }

  getChildMenuItemId(childLabel: string): string {
  const baseId = this.generateIdFromLabel(childLabel || '');
  return `menu-list-item-${baseId}`;
}

  updateActiveStateFromRoute() {
    if (this.item.routerLink) {
      const activeRoute = this.router.isActive(this.item.routerLink[0], (<IsActiveMatchOptions>this.item.routerLinkActiveOptions || { paths: 'exact', queryParams: 'ignored', matrixParams: 'ignored', fragment: 'ignored' }))
      if (activeRoute) {
        this.menuService.onMenuStateChange({ key: this.key, routeEvent: true })
        this.activateParent()
        return
      }
    }
    this.isCurrentRoute = false
  }

  itemClick(event: MouseEvent | KeyboardEvent) {
    // avoid processing disabled items
    if (this.item.id === '99') {
      this.zendeskService.toggleLiveChat()
      return
    }

    if (this.item.disabled) {
      event.preventDefault()
      return;
    }

    // For items with submenu
    if (this.hasSubmenu()) {
      // Simple toggle logic
      if (this.active && this.showSubmenu) {
        // Currently open, so close it
        this.active = false;
        this.showSubmenu = false;
      } else {
        // Currently closed, so open it
        this.active = true;
        this.showSubmenu = true;
      }

      // Don't call menu service for submenu toggles - it might be resetting things
      // this.menuService.onMenuStateChange({ key: this.item.id!, routeEvent: false });

      event.preventDefault();
      return;
    }

    // For regular menu items (no submenu)
    this.active = true;
    this.menuService.onMenuStateChange({ key: this.item.id!, routeEvent: true });
  }

  private navigateToMenu(item: MenuItem) {
    const isDifferentRoute = this.item.routerLink && this.item.routerLink[0] !== this.router.url.substring(1);

    this.router.navigate(item.routerLink).then((navigated: boolean) => {
      if (!navigated) {
        return;
      }

      if (navigated && isDifferentRoute) {
        this.menuService.resetState();
      }
      this.menuService.resetOther(item);
    });
  }

  public activateParent() {
    this.isCurrentRoute = true
    if (this.parentItem) {
      this.parentItem.isCurrentRoute = true
      this.parentItem.activateParent()
    }
  }

  calculatePosition(overlay: HTMLElement, target: HTMLElement) {
    if (overlay) {
      const { top } = target.getBoundingClientRect()
      const vHeight = window.innerHeight
      const oHeight = overlay.offsetHeight
      // reset

      let topPosition = 0
      if ((top + oHeight) >= vHeight) {
        topPosition = (vHeight - (top + oHeight))
        overlay.style.top = `${topPosition}px`
      } else {
        overlay.style.top = '0'
      }
    }
  }

  get submenuAnimation(): string {
    // For root menu items, check if sidebar is expanded
    // For sub-items, just check active and showSubmenu
    let state: string;

    if (this.root) {
      // For root items (like "Detail Reports"), show when not collapsed and active
      state = this.active && this.showSubmenu ? 'visible' : 'hidden';
    } else {
      // For sub-items, always show when parent is active
      state = 'visible';
    }
    return state;
  }

  onSubmenuAnimated(event: AnimationEvent) {
    if (event.toState === 'visible') {
      const el = <HTMLUListElement>event.element
      const elParent = <HTMLUListElement>el.parentElement
      this.calculatePosition(el, elParent)
    }
  }

  hasSubmenu() {
    return this.item.items && this.item.items.length > 0;
  }

}
